"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMenu } from "@/lib/menu/MenuProvider";
import type { MenuDef } from "@/lib/menu/menus";
import { EMOJI_CATS, EMOJIS, Emoji, type EmojiRow } from "./emoji";
import { SearchIcon, SmileIcon } from "./icons";

/* custom media popover — sprite-sheet emoji grid, sticker + gif
   pages share the same small card. text tab bar up top, category
   rail + hover-name footer on the emoji page. stays open after each
   pick like Discord (people chain emojis) — esc/scrim closes it */

export type PickerTab = "emojis" | "stickers" | "gifs";

export function EmojiPicker({
  onPick,
  mobile = false,
  tab: initTab = "emojis",
}: {
  onPick: (char: string) => void;
  mobile?: boolean;
  tab?: PickerTab;
}) {
  const [tab, setTab] = useState<PickerTab>(initTab);
  const [q, setQ] = useState("");
  const [hovered, setHovered] = useState<EmojiRow | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  /* the spinner covers the page WHILE it loads — content mounts
     hidden and fetches immediately, the overlay lifts when the page
     reports ready (with a 500ms floor so it never flickers).
     render-time reset on tab change → no sync-effect setState */
  const [minElapsed, setMinElapsed] = useState(false);
  const [ready, setReady] = useState(false);
  const [seen, setSeen] = useState(tab);
  if (seen !== tab) {
    setSeen(tab);
    setMinElapsed(false);
    setReady(false);
  }
  useEffect(() => {
    if (minElapsed) return;
    const t = setTimeout(() => setMinElapsed(true), 500);
    return () => clearTimeout(t);
  }, [minElapsed]);
  const loading = !(minElapsed && ready);
  const handleReady = useCallback(() => setReady(true), []);

  /* page height — on mobile all three tabs share one size so the docked
     panel never resize-jumps when switching; desktop keeps the tighter
     emoji grid since the floating card doesn't shove layout */
  const gridH = mobile || tab !== "emojis" ? "h-[308px]" : "h-64";

  /* emoji readiness = sprite sheet in cache (near-instant after
     first open — the 500ms floor still applies) */
  useEffect(() => {
    if (tab !== "emojis") return;
    const im = new Image();
    im.onload = () => setReady(true);
    im.onerror = () => setReady(true);
    im.src = "/emoji/sheet.png";
  }, [tab]);

  /* emoji/sticker name filter — the search bar under the tabs drives
     the flat results grid on both pages; gifs query the proxy */
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? EMOJIS.filter((r) => r.n.includes(t)) : null;
  }, [q]);

  return (
    <div className="flex flex-col">
      {/* the three media cats — pill tabs, one card does it all */}
      <nav
        className={`mb-2 flex gap-1 rounded-lg bg-ink/60 p-1 ring-1 ring-line ${
          mobile ? "mt-1.5" : "-mt-0.5"
        }`}
      >
        {(["emojis", "stickers", "gifs"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`flex-1 rounded-md px-2 py-1.5 text-note font-semibold capitalize transition ${
              tab === t
                ? "bg-raise text-fg"
                : "text-fg-mute hover:text-fg"
            }`}
          >
            {t}
          </button>
        ))}
      </nav>
      {/* divider between the tabs and the search */}
      <div className="mx-1 mb-1.5 border-b border-line" />

      {/* search — one bar serves every tab: names for emojis +
         stickers, gifstash search for gifs */}
      <div className="mb-2 flex items-center gap-2 rounded-lg bg-field px-2.5 py-2 ring-1 ring-line transition focus-within:ring-accent/60">
        <SearchIcon className="h-4 w-4 shrink-0 text-fg-faint" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={
            tab === "emojis"
              ? "search emoji"
              : `Search GifStach for ${tab}`
          }
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          className="min-w-0 flex-1 bg-transparent text-note text-fg outline-none placeholder:text-fg-faint"
        />
        {tab === "emojis" ? null : (
          <a
            href="https://gifstach.sh"
            target="_blank"
            rel="noopener noreferrer"
            className="flex shrink-0 items-baseline gap-1 transition hover:brightness-125"
            title="GifStach — by Isolation"
          >
            <span className="text-[10px] font-bold tracking-wide text-fg-dim">GifStach</span>
            <span className="text-[8px] font-medium tracking-wide text-fg-faint">(By ISO)</span>
          </a>
        )}
      </div>

      {/* sticker / gif pages — mock grids until the media pipeline
          lands; the card + rail already scale to them */}
      <div className="relative">
        {loading ? (
          <div className={`absolute inset-0 z-10 grid place-items-center ${gridH}`}>
            <span className="spin-arc" />
          </div>
        ) : null}
        <div className={loading ? "invisible" : ""}>
      {tab === "stickers" || tab === "gifs" ? (
        <GifSackPage kind={tab} onPick={onPick} q={q} setQ={setQ} onReady={handleReady} />
      ) : filtered ? (
        <div className={`scroll-dark grid ${gridH} touch-pan-y grid-cols-8 content-start gap-0.5 overflow-y-auto overscroll-contain`}>
          {filtered.length === 0 ? (
            <p className="col-span-8 py-10 text-center text-note text-fg-mute">
              nothing for “{q}”
            </p>
          ) : (
            filtered.map((r) => (
              <Cell key={r.c} r={r} onPick={onPick} setHover={setHovered} />
            ))
          )}
        </div>
      ) : (
        <div className={`flex ${gridH} gap-1`}>
          {/* category rail — the first emoji of each group doubles as
              its icon; tap to jump, scroll highlights where you are */}
          <nav className="scroll-dark flex w-10 shrink-0 touch-pan-y flex-col gap-0.5 overflow-y-auto overscroll-contain border-r border-line pr-1">
            {EMOJI_CATS.map((cat) => (
              <button
                key={cat}
                type="button"
                aria-label={cat}
                title={cat}
                onClick={() =>
                  gridRef.current
                    ?.querySelector(`[data-cat="${cat}"]`)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" })
                }
                className="grid h-8 w-full place-items-center rounded-md transition hover:bg-raise active:scale-90"
              >
                <Emoji char={EMOJIS.find((r) => r.cat === cat)!.e} size={16} />
              </button>
            ))}
          </nav>
          <div
            ref={gridRef}
            className="scroll-dark min-w-0 flex-1 touch-pan-y overflow-y-auto overscroll-contain pr-1"
          >
            {EMOJI_CATS.map((cat) => (
              <section key={cat} data-cat={cat} className="mb-1.5 last:mb-0">
                <p className="sticky top-0 bg-slab px-1 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-fg-faint">
                  {cat}
                </p>
                <div className="grid grid-cols-8 gap-0.5">
                  {EMOJIS.filter((r) => r.cat === cat).map((r) => (
                    <Cell
                      key={r.c}
                      r={r}
                      onPick={onPick}
                      setHover={setHovered}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
        </div>
      </div>

      {/* footer — hovered emoji preview, desktop only (nothing to
          hover on mobile — long-press/right-click pops the name).
          fixed height so the card never resizes as you sweep */}
      {mobile || tab !== "emojis" ? null : (
        <div className="mt-2 flex h-11 items-center gap-2 rounded-lg bg-ink/60 px-2.5 py-1.5 ring-1 ring-line">
          {hovered ? (
            <>
              <Emoji char={hovered.e} size={18} />
              <span className="truncate text-micro text-fg-mute">
                :{hovered.n.replace(/ /g, "_")}:
              </span>
            </>
          ) : (
            <>
              <SmileIcon className="h-4 w-4 text-fg-faint" />
              <span className="truncate text-micro text-fg-faint">
                pick one — it stays open, chain them
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Cell({
  r,
  onPick,
  setHover,
}: {
  r: EmojiRow;
  onPick: (char: string) => void;
  setHover: (r: EmojiRow | null) => void;
}) {
  const menu = useMenu();
  return (
    <button
      type="button"
      aria-label={r.n}
      onClick={() => onPick(r.e)}
      /* right-click / mobile long-press → the emoji's name card */
      onContextMenu={(e) => {
        e.preventDefault();
        menu.openAt(emojiInfoDef(r, onPick), e.clientX, e.clientY);
      }}
      onPointerEnter={() => setHover(r)}
      onPointerLeave={() => setHover(null)}
      className="grid h-10 w-full place-items-center rounded-md transition hover:bg-raise active:scale-90"
    >
      <Emoji char={r.e} size={26} />
    </button>
  );
}

/* real gifs through gif.sack — the /gifs route owns the jwt
   server-side (client never mints or holds tokens) and queries
   tenor's web api. picks get registered → the message carries a
   cdn.isolation.chat url, not a tenor one */
interface GifItem {
  id: string;
  url: string;
  preview: string;
  /* progressive-load variants — same regId/dims, type+q swapped out */
  thumb: string;
  still: string;
  /* source aspect ratio — drives the sticker grid's span logic */
  ar: number;
}

/* tenor v2 result — only what the grid needs */
interface TenorResult {
  id: string | number;
  media_formats?: Record<string, { url?: string; dims?: number[] } | undefined>;
}

/* cdn urls are /media/clientContent/{kind}/{regId}/{W}x{H}/{type}/?q=
   — dims, type, quality are all just path segments, so variants are
   a swap */
const variant = (u: string, type: string, q: string, dims?: string) => {
  const v = u.replace(/\/(gif|png|webp|svg|sgif|jpg)\/\?q=\w+$/, `/${type}/?q=${q}`);
  return dims ? v.replace(/\/\d+x\d+\//, `/${dims}/`) : v;
};

function toGifItem(it: TenorResult): GifItem | null {
  const mf = it.media_formats ?? {};
  const url = mf.gif?.url ?? mf.webp?.url ?? mf.mediumgif?.url;
  const preview = mf.tinygif?.url ?? mf.nanogif?.url ?? mf.tinywebp?.url ?? url;
  const dims = (mf.gif ?? mf.webp ?? mf.mediumgif ?? mf.tinygif)?.dims;
  /* 5x5 png first frame = the blur-up thumb (a few hundred bytes,
     paints before anything else); still = preview dims static png */
  return url && preview
    ? {
        id: String(it.id),
        url,
        preview,
        thumb: variant(preview, "png", "lo", "5x5"),
        still: variant(preview, "png", "m"),
        ar: dims?.[0] && dims?.[1] ? dims[0] / dims[1] : 1,
      }
    : null;
}


/* progressive gif tile — three stacked layers paint in order of size:
   1. thumb — 5x5 png, stretched+blurred, loads almost instantly
      (a few hundred bytes)
   2. still — preview-dims png first frame; if it lands before the gif
      the tile just sits static until the gif fades in
   3. the gif — the big boy. starts opacity-0, fades in on load so it
      never pops over the placeholder.
   off-screen tiles unmount the gif layer entirely — an animated gif
   keeps every decoded frame resident, so a long grid of them eats
   memory alive. scrolling back remounts it from the immutable cache;
   the still sits underneath so the handoff never flashes */
function GifTile({ g }: { g: GifItem }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [loaded, setLoaded] = useState(false);
  /* false until the observer says otherwise — off-screen gifs never
     even mount their img. unload is debounced so flicking through the
     grid doesn't thrash mount/decode on every tile */
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let t: ReturnType<typeof setTimeout> | undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          clearTimeout(t);
          setVisible(true);
        } else {
          t = setTimeout(() => setVisible(false), 600);
        }
      },
      /* 300px runway — gifs join ahead of the fold */
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => {
      clearTimeout(t);
      io.disconnect();
    };
  }, []);
  return (
    <span ref={ref} className="relative block aspect-square w-full overflow-hidden">
      {/* 5x5 stretched = a free color blob, no blur filter needed —
          blur-md was the scroll lag: a full-screen repaint of ~30
          blurred surfaces every frame. unmounts once the gif is in */}
      {!loaded && (
        /* eslint-disable-next-line @next/next/no-img-element -- remote preview */
        <img
          src={g.thumb}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* the still stays mounted — it's what shows while the gif is
          frozen mid-scroll (visibility:hidden) or unloaded off-screen.
          one decoded frame, cheap to keep */}
      {/* eslint-disable-next-line @next/next/no-img-element -- remote preview */}
      <img
        src={g.still}
        alt=""
        aria-hidden
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover"
      />
      {visible ? (
        /* eslint-disable-next-line @next/next/no-img-element -- remote preview */
        <img
          src={g.preview}
          alt="gif"
          decoding="async"
          onLoad={() => setLoaded(true)}
          className={`gif-layer absolute inset-0 aspect-square h-full w-full object-cover transition-opacity duration-300 group-hover:scale-105 ${
            loaded ? "opacity-100" : "opacity-0"
          }`}
        />
      ) : null}
    </span>
  );
}

/* each kind gets its own quick cats — sticker-flavored queries for
   stickers, meme-ish ones for gifs */
const GIF_CATS = ["Popular", "Love", "Memes", "Sleep"] as const;
const STICKER_CATS = ["Cute", "Funny", "Cool", "Angry"] as const;

/* gif.sack gifs + stickers — queries ride /gifs/query/{op}?s=&f=
   (the route holds the gifstash jwt server-side). the four quick
   cats run searches; their tiles carry a live preview. picked gifs
   register via /gifs/register → ![](cdn url) — the md renderer
   embeds them */
function GifSackPage({
  kind,
  q,
  setQ,
  onPick,
  onReady,
}: {
  kind: "gifs" | "stickers";
  q: string;
  setQ: (s: string) => void;
  onPick: (c: string) => void;
  onReady?: () => void;
}) {
  const [gifs, setGifs] = useState<GifItem[] | null>(null);
  const [ms, setMs] = useState(0);
  const [next, setNext] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const CATS = kind === "stickers" ? STICKER_CATS : GIF_CATS;

  /* freeze gifs while scrolling — playing gifs repaint every frame,
     and moving them compounds into dropped frames. toggling a css
     class on the scroller costs nothing per frame vs re-rendering
     every tile through state */
  const scroller = useRef<HTMLDivElement>(null);
  const scrollIdle = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(scrollIdle.current), []);
  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    el.classList.add("gif-scrolling");
    clearTimeout(scrollIdle.current);
    scrollIdle.current = setTimeout(() => el.classList.remove("gif-scrolling"), 150);
  };

  /* the quick cats just fill the search box — one query path */
  const query = q.trim();

  /* gifstash url builder — s = search string, f = filter json
     (sf=sticker, limit, pos pagination). /gifs owns the jwt
     server-side — the client never mints or holds tokens */
  const stash = (op: "search" | "feat", extra: Record<string, string> = {}) => {
    const f = { limit: 36, ...(kind === "stickers" ? { sf: "sticker" } : {}), ...extra };
    return `/gifs/query/${op}?${query ? `s=${encodeURIComponent(query)}&` : ""}f=${encodeURIComponent(
      JSON.stringify(f),
    )}`;
  };

  useEffect(() => {
    let dead = false;
    (async () => {
      /* past the first await → not a sync setState (lint-safe) */
      setGifs(null);
      setNext("");
      const t0 = performance.now();
      const body = await fetch(stash(query ? "search" : "feat")).then((r) => r.json());
      if (!dead) {
        setGifs(((body.results ?? []) as TenorResult[]).map(toGifItem).filter((g): g is GifItem => !!g));
        setNext(body.next ?? "");
        setMs(Math.round(performance.now() - t0));
        setErr(body.error ?? null);
        onReady?.();
      }
    })().catch(() => {
      setGifs([]);
      onReady?.();
    });
    return () => {
      dead = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stash closes over token; token arrives inside
  }, [query, kind, onReady]);

  /* show more — tenor paginates with the opaque `next` cursor */
  async function more() {
    if (!next) return;
    const body = await fetch(stash(query ? "search" : "feat", { pos: next, limit: "30" })).then((r) => r.json());
    setGifs((g) => [
      ...(g ?? []),
      ...(((body.results ?? []) as TenorResult[]).map(toGifItem).filter((x): x is GifItem => !!x)),
    ]);
    setNext(body.next ?? "");
  }

  /* picks are instant — /gifs already registered every result, so
     g.url IS the cdn url and g.id IS our regId. nothing to mint,
     nothing to register */
  function pick(g: GifItem) {
    onPick(`![${kind === "stickers" ? "sticker" : "gif"}](${g.url})`);
  }

  /* base page = ONLY the four cat tiles (2×2 fills the card); the
     results grid appears once you search or press a cat. tile art
     reuses the trending fetch — no extra upstream calls */
  if (!query)
    return (
      <div className="grid h-[308px] grid-cols-2 grid-rows-2 gap-1.5">
        {CATS.map((c, i) => (
          <button
            key={c}
            type="button"
            onClick={() => setQ(c)}
            className="group relative grid place-items-center overflow-hidden rounded-lg ring-1 ring-line transition hover:ring-accent/60 active:scale-[0.98]"
          >
            {gifs?.[i] ? (
              // eslint-disable-next-line @next/next/no-img-element -- remote preview
              <img
                src={gifs[i].preview}
                alt=""
                className={`absolute inset-0 h-full w-full object-cover opacity-50 transition group-hover:scale-105 group-hover:opacity-70 ${
                  kind === "stickers" ? "object-contain p-3" : ""
                }`}
              />
            ) : null}
            <span className="relative z-10 text-note font-semibold text-fg drop-shadow">
              {c}
            </span>
          </button>
        ))}
      </div>
    );

  return (
    <div ref={scroller} onScroll={onScroll} className="scroll-dark flex h-[308px] touch-pan-y flex-col overflow-y-auto overscroll-contain">
      {/* the active search — divider splits it from the results */}
      <div className="mb-1 flex shrink-0 items-center gap-2 border-b border-line pb-1.5">
        <button
          type="button"
          onClick={() => setQ("")}
          aria-label="Back to categories"
          className="grid h-6 w-6 place-items-center rounded-md text-fg-faint transition hover:bg-raise hover:text-fg"
        >
          ←
        </button>
        <span className="truncate text-note font-semibold text-fg-mute">
          {gifs === null
            ? "searching…"
            : `${gifs.length} result${gifs.length === 1 ? "" : "s"} in ${ms}ms`}
        </span>
      </div>
      {gifs === null ? (
        <div className="grid flex-1 place-items-center">
          <span className="spin-arc" />
        </div>
      ) : err ? (
        <p className="py-10 text-center text-note text-fg-mute">{err}</p>
      ) : !gifs.length ? (
        <p className="py-10 text-center text-note text-fg-mute">
          no {kind}
        </p>
      ) : (
        <div className={`grid gap-1 ${kind === "stickers" ? "grid-cols-4" : "grid-cols-2"}`}>
          {gifs.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => pick(g)}
              /* content-visibility: the browser skips painting tiles
                 that are fully off-screen — the single biggest scroll
                 win on a media grid */
              style={{ contentVisibility: "auto", containIntrinsicSize: "140px" }}
              className={`group rounded-lg transition-transform active:scale-95 ${
                kind === "stickers"
                  ? "grid aspect-square place-items-center overflow-hidden p-1.5 hover:bg-raise"
                  : "overflow-hidden ring-1 ring-line hover:ring-accent/60"
              }`}
            >
              {kind === "stickers" ? (
                /* the cdn renders sticker previews square — 256x256
                   contain pads the short side instead of squishing,
                   so every card is the same footprint and the sticker
                   is as big as its own shape allows */
                /* eslint-disable-next-line @next/next/no-img-element -- remote preview */
                <img
                  src={variant(g.preview, "webp", "m", "256x256")}
                  alt="sticker"
                  loading="lazy"
                  className="h-full w-full object-contain transition group-hover:scale-110"
                />
              ) : (
                <GifTile g={g} />
              )}
            </button>
          ))}
        </div>
      )}
      {next ? (
        <button
          type="button"
          onClick={more}
          className="mt-1.5 w-full shrink-0 rounded-lg bg-field py-2 text-note font-medium text-fg-dim ring-1 ring-line transition hover:bg-raise hover:text-fg"
        >
          Show more
        </button>
      ) : null}
    </div>
  );
}

/* right-click info card — the emoji blown up + its :name:, tap to
   insert. no hover on mobile, so this is the only way to read a
   name there (and it's the desktop shortcut too) */
const emojiInfoDef = (r: EmojiRow, onPick: (c: string) => void): MenuDef => ({
  id: `emoji-info-${r.c}`,
  title: r.n,
  bare: true,
  Content: function EmojiInfo() {
    const menu = useMenu();
    return (
      <button
        type="button"
        onClick={() => {
          onPick(r.e);
          menu.close();
        }}
        className="flex w-full items-center gap-3 rounded-lg p-2 text-left transition hover:bg-raise"
      >
        <Emoji char={r.e} size={40} />
        <span className="min-w-0">
          <span className="block truncate text-note font-medium text-fg">
            :{r.n.replace(/ /g, "_")}:
          </span>
          <span className="block text-micro text-fg-faint">
            {r.cat} · tap to insert
          </span>
        </span>
      </button>
    );
  },
});

/* factory — the composer's smiley/sticker/gif buttons open this
   dropup on their tab; onPick writes the char into the draft */
export const emojiDef = (
  onPick: (char: string) => void,
  tab: PickerTab = "emojis",
): MenuDef => ({
  id: `picker-${tab}`,
  title: "",
  bare: true,
  Content: () => <EmojiPicker onPick={onPick} tab={tab} />,
  dropdownW: 440,
  /* planned spot — always opens above the composer, never anchored to
     whichever button spawned it */
  spot: "br",
});
